/**
 * Integrity detectors. Evidence must answer who, when, and how.
 */
import type { IntegrityAuditEvent, IntegrityHouseRow, IntegritySignal } from './integritySignals';

const SENSITIVE_ACTIONS = new Set([
  'play.create',
  'plays.create',
  'config.update',
  'route.collect',
  'results.publish',
  'results.update',
]);

function eventTs(event: IntegrityAuditEvent, fallbackIndex: number): number {
  if (typeof event.ts === 'number') return event.ts;
  return Date.now() - fallbackIndex * 60_000;
}

function houseIdFromEvent(event: IntegrityAuditEvent): string {
  return event.actor?.houseId ?? '';
}

function snapshotActor(actor?: IntegrityAuditEvent['actor']) {
  if (!actor) return undefined;
  const snap: NonNullable<IntegrityAuditEvent['actor']> = {};
  if (actor.uid) snap.uid = actor.uid;
  if (actor.name) snap.name = actor.name;
  if (actor.role) snap.role = actor.role;
  if (actor.houseId) snap.houseId = actor.houseId;
  return Object.keys(snap).length ? snap : undefined;
}

function snapshotEvent(event: IntegrityAuditEvent, ts: number) {
  const row: Record<string, unknown> = { ts };
  if (event.id) row.id = event.id;
  if (event.action) row.action = event.action;
  if (event.msg) row.msg = event.msg;
  const actor = snapshotActor(event.actor);
  if (actor) row.actor = actor;
  return row;
}

/** Lister inscribes plays shortly after runner reported collection. */
export function detectPlaysAfterCollection(
  events: IntegrityAuditEvent[],
  windowMs = 5 * 60_000,
): IntegritySignal[] {
  const sorted = [...events].sort((a, b) => eventTs(a, 0) - eventTs(b, 0));

  for (let i = 0; i < sorted.length; i += 1) {
    const event = sorted[i];
    if (event.action !== 'route.collect') continue;
    if (event.actor?.role !== 'runner') continue;

    const houseId = houseIdFromEvent(event);
    if (!houseId) continue;

    const collectTs = eventTs(event, i);
    for (let j = i + 1; j < sorted.length; j += 1) {
      const next = sorted[j];
      const ts = eventTs(next, j);
      if (ts - collectTs > windowMs) break;
      if (houseIdFromEvent(next) !== houseId) continue;
      if (next.action !== 'play.create' && next.action !== 'plays.create') continue;
      if (next.actor?.role !== 'lister') continue;

      return [{
        kind: 'conspiracy',
        severity: 'danger',
        code: 'plays_after_collection',
        houseId,
        messageKey: 'integrity_plays_after_collection',
        messageParams: { houseId },
        evidence: {
          collectActor: snapshotActor(event.actor) ?? {},
          playActor: snapshotActor(next.actor) ?? {},
          windowMs: ts - collectTs,
          collectTs,
          playTs: ts,
          events: [snapshotEvent(event, collectTs), snapshotEvent(next, ts)],
        },
      }];
    }
  }

  return [];
}

/** @deprecated Use detectPlaysAfterCollection. */
export const detectListerRunnerCollusion = detectPlaysAfterCollection;

/** Collection reported without recent play activity on the same house. */
export function detectCollectionWithoutPlays(
  events: IntegrityAuditEvent[],
  lookbackMs = 30 * 60_000,
): IntegritySignal[] {
  const sorted = [...events].sort((a, b) => eventTs(a, 0) - eventTs(b, 0));

  for (let i = 0; i < sorted.length; i += 1) {
    const event = sorted[i];
    if (event.action !== 'route.collect') continue;

    const houseId = houseIdFromEvent(event);
    if (!houseId) continue;

    const collectTs = eventTs(event, i);
    let hasRecentPlay = false;

    for (let j = 0; j < sorted.length; j += 1) {
      const candidate = sorted[j];
      const ts = eventTs(candidate, j);
      if (ts > collectTs) break;
      if (collectTs - ts > lookbackMs) continue;
      if (houseIdFromEvent(candidate) !== houseId) continue;
      if (candidate.action === 'play.create' || candidate.action === 'plays.create') {
        hasRecentPlay = true;
        break;
      }
    }

    if (!hasRecentPlay) {
      return [{
        kind: 'corruption',
        severity: 'danger',
        code: 'collection_without_plays',
        houseId,
        messageKey: 'integrity_collection_without_plays',
        messageParams: { houseId },
        evidence: {
          collectActor: snapshotActor(event.actor) ?? {},
          lookbackMs,
          collectTs,
          events: [snapshotEvent(event, collectTs)],
        },
      }];
    }
  }

  return [];
}

/** Ledger skew — coins out at or above coins in on active houses. */
export function detectLedgerSkew(houses: IntegrityHouseRow[]): IntegritySignal[] {
  return houses.flatMap((house) => {
    const coinsIn = Number(house.coinsIn ?? 0);
    const coinsOut = Number(house.coinsOut ?? 0);
    if (house.status === 'suspended' || coinsIn <= 0) return [];

    const ratio = coinsOut / coinsIn;
    if (coinsOut < coinsIn) return [];

    return [{
      kind: 'corruption' as const,
      severity: 'danger' as const,
      code: 'ledger_skew',
      houseId: house.id,
      messageKey: 'integrity_ledger_skew',
      messageParams: { house: house.name ?? house.id, ratio: Math.round(ratio * 100) },
      evidence: { coinsIn, coinsOut, ratio, houseName: house.name ?? house.id },
    }];
  });
}

/** Activity on a suspended house — possible sabotage or bypass. */
export function detectSuspendedHouseActivity(
  events: IntegrityAuditEvent[],
  houses: IntegrityHouseRow[],
): IntegritySignal[] {
  const suspended = new Set(
    houses.filter((h) => h.status === 'suspended').map((h) => h.id),
  );
  if (suspended.size === 0) return [];

  for (let i = 0; i < events.length; i += 1) {
    const event = events[i];
    const houseId = houseIdFromEvent(event);
    if (!houseId || !suspended.has(houseId)) continue;

    const action = event.action ?? '';
    if (!SENSITIVE_ACTIONS.has(action)) continue;

    const ts = eventTs(event, i);
    return [{
      kind: 'sabotage',
      severity: 'danger',
      code: 'suspended_house_activity',
      houseId,
      messageKey: 'integrity_suspended_house_activity',
      messageParams: { houseId, action },
      evidence: {
        action,
        actor: snapshotActor(event.actor) ?? {},
        ts,
        events: [snapshotEvent(event, ts)],
      },
    }];
  }
  return [];
}

/** Rapid config changes by non-banker staff. */
export function detectConfigTamperBurst(
  events: IntegrityAuditEvent[],
  windowMs = 10 * 60_000,
  threshold = 3,
): IntegritySignal[] {
  const configEvents = events
    .map((event, index) => ({ event, ts: eventTs(event, index) }))
    .filter(({ event }) => (event.action ?? '').includes('config'))
    .filter(({ event }) => event.actor?.role !== 'banker');

  for (let i = 0; i < configEvents.length; i += 1) {
    const { event: anchor, ts: anchorTs } = configEvents[i];
    const houseId = houseIdFromEvent(anchor);
    if (!houseId) continue;

    const burst: ReturnType<typeof snapshotEvent>[] = [];
    for (let j = i; j < configEvents.length; j += 1) {
      const { event, ts } = configEvents[j];
      if (ts - anchorTs > windowMs) break;
      if (houseIdFromEvent(event) !== houseId) continue;
      burst.push(snapshotEvent(event, ts));
    }

    if (burst.length >= threshold) {
      return [{
        kind: 'sabotage',
        severity: 'warn',
        code: 'config_tamper_burst',
        houseId,
        messageKey: 'integrity_config_tamper_burst',
        messageParams: { houseId, count: burst.length },
        evidence: {
          count: burst.length,
          windowMs,
          actorRole: anchor.actor?.role,
          events: burst,
        },
      }];
    }
  }
  return [];
}

/** Play activity shortly after results publish. */
export function detectPostResultPlays(events: IntegrityAuditEvent[], windowMs = 5 * 60_000): IntegritySignal[] {
  const sorted = [...events].sort((a, b) => eventTs(a, 0) - eventTs(b, 0));

  for (let i = 0; i < sorted.length; i += 1) {
    const event = sorted[i];
    if (!(event.action ?? '').includes('results')) continue;

    const houseId = houseIdFromEvent(event);
    if (!houseId) continue;

    const publishTs = eventTs(event, i);
    for (let j = i + 1; j < sorted.length; j += 1) {
      const next = sorted[j];
      const ts = eventTs(next, j);
      if (ts - publishTs > windowMs) break;
      if (houseIdFromEvent(next) !== houseId) continue;
      if (!(next.action ?? '').includes('play')) continue;

      return [{
        kind: 'corruption',
        severity: 'danger',
        code: 'post_result_play',
        houseId,
        messageKey: 'integrity_post_result_play',
        messageParams: { houseId },
        evidence: {
          publishAction: event.action,
          playAction: next.action,
          actorRole: next.actor?.role,
          publishActor: snapshotActor(event.actor) ?? {},
          playActor: snapshotActor(next.actor) ?? {},
          publishTs,
          playTs: ts,
          windowMs: ts - publishTs,
          events: [snapshotEvent(event, publishTs), snapshotEvent(next, ts)],
        },
      }];
    }
  }
  return [];
}
